import type { FC } from "react";
import { useMemo, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch, useSelector } from "react-redux";

import { EKeywordTabs, SourceTypeMap } from "app/UtilitiesAndConstants/Constants/SourceTypes";
import { ArrowKeywordWrapper, StyledArrowIcon } from "app/components/React/Table/Utils/quickNavStyledComponents";

import { translate } from "app/filters/i18nFilter";
import { showErrorToast, showSuccessToast } from "app/actions/toast_actions";
import getTableColumns from "./TableColumn";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import { apiHelper } from "app/common/services/apiHelper";
import { defaultParams } from "app/Shared/defaults";
import { getUrlComponents } from "pro-features/components/AutocompleteWebsites/urlComponentUtils";
import { transformSiteListToParam } from "pro-features/components/AutocompleteWebsites/utils";
import { WebsitePageSearchTypeNames } from "app/pages/digital-marketing/keywords-campaigns/wizard/constants";

import { getTableSelectedItems } from "app/reducers/_reducers/tableSelection.reducer";
import { createUniqueListOfAllDomains } from "pro-features/components/DomainsFilter/Utils";
import { SWReactTableWrapperWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { DomainSelectionWrapper } from "app/components/React/TableSelectionComponents/DomainSelectionWrapper";
import { timeGranularity } from "app/components/widget/widget-utilities/time-granularity";
import type { ActivationObjectSchema } from "app/pages/activation/types/api";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import { DEVICES_TYPES_FOR_API } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import { keywordGroupsTransforms } from "app/services/keyword-groups";
import type { EntityKey } from "app/common/keyword-research/serp-players/consts";

import { webSourceToDeviceMap } from "app/common/keyword-research/clicks-over-time-graph/graphTypes";

import { OrganicTableEnriched } from "./OrganicTableEnriched";
import { TableTop } from "./TableTop";
import { isPaidSearchPagesAvailable } from "app/pages/keyword-research/utils/permissions";

const TABLE_SELECTION_PROPERTY = "Domain";
const TABLE_PAGE_SIZE = 100;
export const WEBSITE_KEYWORDS_STATE = "organicsearch_websites_v2_traffic_keywords";

export interface ITablePropsParams {
    keyword?: string;
    webSource?: string;
    duration: string;
    country: number;
    keys: string;
    mtd?: string;
    IncludeOrganic?: string;
    IncludePaid?: string;
    ampFilter?: string;
    Device?: string;
    GroupHash?: string;
}

interface ITableProps {
    getDataCallback: (records) => void;
    isKeywordsGroup: boolean;
    tableSelectionKey: string;
    initialSelectedRowsCount?: number;
    addToDashboardMetric: string;
    tableEndpoint: string;
    enrichTableEndpoint: string;
    visibleColumns: string[];
    visibleColumnsEnrich: string[];
    dropDownColumnsTexts: Record<string, string>;
    entity: EntityKey;
    dashboardMetric: string;
    transformData: (data) => void;
    transformDataEnrich?: (data) => void;
    hideAddToDashboard?: boolean;
    selectedRows?: string[];
    hideExcel?: boolean;
    tableHeaderTooltip?: string;
    removeLinkFromEnrichTable?: boolean;
    activationSets?: ActivationObjectSchema[];
    websiteKeywordsState?: string;
    keywordAdsState?: string;
    keywordSerpPlayersState?: string;
}

export interface ISort {
    field: string;
    sortDirection: string;
}

export enum EColumnActions {
    Explore,
    Copy,
    Open,
}

interface DropdownColumnActionsItem {
    name: string;
    id: string;
    iconName: string;
    text: string;
    iconColor: string;
    children: string;
    textIndent: boolean;
}
const defaultSort = { field: "Share", sortDirection: "asc" };

export const TableWrapper = styled.div`
    ${ArrowKeywordWrapper} {
        padding-top: 2px;
    }
    .active-row {
        .presentJustOnHover {
            ${ArrowKeywordWrapper} {
                cursor: pointer;
                background-color: ${colorsPalettes.blue[100]};
                ${StyledArrowIcon} {
                    visibility: visible;
                }
            }
        }
    }
    .keywords-table .DropdownContent-container {
        z-index: 1073 !important;
        margin-top: 0 !important;
    }
`;

const getSitesForApi = (domain: string, subDomains: { Domain: string }[] | unknown) => {
    const sitesName = [domain];
    if (Array.isArray(subDomains)) {
        subDomains.forEach((site) => {
            sitesName.push(site.Domain);
        });
    }
    const sitesString = sitesName.join(",");
    return sitesString;
};

export const DomainsTable: FC<ITableProps> = (props) => {
    const { params } = useCurrentStateAndParams();
    const { webSource, country, selectedPageTab, duration, category, funcFlag, domains } = params;
    const { websiteKeywordsState = WEBSITE_KEYWORDS_STATE, keywordAdsState, keywordSerpPlayersState } = props;
    const {
        tableEndpoint,
        enrichTableEndpoint,
        visibleColumns,
        dropDownColumnsTexts,
        visibleColumnsEnrich,
        transformData,
        transformDataEnrich,
        tableHeaderTooltip = "mobile.keywords.2.table.title.tooltip",
        removeLinkFromEnrichTable = false,
        entity,
        activationSets,
    } = props;
    const selectedRows = useSelector(getTableSelectedItems(props.tableSelectionKey));

    const [pageNumber, setPageNumber] = useState(0);
    const [sort, setSort] = useState<ISort>();
    const isKeywordGroup = keywordGroupsTransforms.isKeywordGroup(params.keyword);
    const dispatch = useDispatch();

    const changePageCallback = (page) => {
        setPageNumber(page);
    };
    const initialFilters = useMemo(() => {
        const keywordGroupForApi = isKeywordGroup ? params.keyword.slice(1) : params.keyword;
        const allDomains = createUniqueListOfAllDomains(domains?.split(","), activationSets);
        const paramsForTableApi = {
            ...(params as ITablePropsParams),
            keys: keywordGroupForApi,
            webSource: params.webSource,
            Device: webSourceToDeviceMap[params.webSource],
            timeGranularity: timeGranularity.monthly,
            includeSubDomains: true,
            sourceType: SourceTypeMap[params.selectedPageTab],
            category,
            funcFlag,
            DomainsFilter: allDomains,
        };
        delete paramsForTableApi.keyword;
        const apiParams = apiHelper.transformParamsForAPI(paramsForTableApi);
        apiParams.sort = defaultSort.field;
        apiParams.asc = false;
        apiParams.category = apiParams.category === defaultParams.category ? "" : apiParams.category;

        return apiParams;
    }, [params]);

    const onSort = ({ field, sortDirection }) => {
        setSort({ field, sortDirection });
    };

    const handleUrlsTrackingClick = (item: DropdownColumnActionsItem) => {
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Quick Nav/${item.name}/${item.text}`);
    };

    const onNavItemClick = async (item: DropdownColumnActionsItem) => {
        handleUrlsTrackingClick(item);
        const siteList = getUrlComponents(item.id);
        const url = transformSiteListToParam([
            {
                url: item.id,
                searchType: WebsitePageSearchTypeNames.ExactUrl,
            },
        ]);
        if (item.name === EColumnActions[EColumnActions.Explore]) {
            proNavigator.go(websiteKeywordsState, {
                ...params,
                key: item.id.split(",")[0],
                pageFilter: url,
                isWWW: "*",
                selectedPageTab:
                    !isPaidSearchPagesAvailable() && selectedPageTab === EKeywordTabs[EKeywordTabs.Paid]
                        ? EKeywordTabs[EKeywordTabs.Total]
                        : selectedPageTab,
            });
        } else if (item.name === EColumnActions[EColumnActions.Copy]) {
            try {
                await navigator.clipboard.writeText(item.id);
                dispatch(showSuccessToast(translate(dropDownColumnsTexts.successToast)));
            } catch {
                dispatch(showErrorToast(translate(dropDownColumnsTexts.errorToast)));
            }
        } else {
            window.open(`//${item.id}`, "_blank");
        }
    };

    const tableColumnsParams = {
        isGroup: isKeywordGroup,
        isEnrichTable: false,
        sortableFields: ["Share", "Change", "Position"],
        sortedColumn: sort,
        isTotal: webSource === DEVICES_TYPES_FOR_API.TOTAL,
        showPosition: country !== "999",
        selectedPageTab,
        onNavItemClick,
        visibleColumns,
        duration,
        country,
        entity,
        keywordAdsState,
        keywordSerpPlayersState,
    };

    const tableColumns = useMemo(() => {
        return getTableColumns(tableColumnsParams);
    }, [sort]);

    return (
        <TableWrapper>
            <SWReactTableWrapperWithSelection
                key={params.selectedPageTab}
                tableSelectionKey={props.tableSelectionKey}
                tableSelectionProperty={TABLE_SELECTION_PROPERTY}
                maxSelectedRows={props.initialSelectedRowsCount}
                cleanOnUnMount={true}
                deleteTableSelectionOnUnMount={true}
                transformData={transformData}
                serverApi={tableEndpoint}
                initialFilters={initialFilters}
                tableColumns={tableColumns}
                changePageCallback={changePageCallback}
                tableOptions={{
                    tableSelectionTrackingParam: TABLE_SELECTION_PROPERTY,
                    trackName: "Traffic Sources",
                    noDataTitle: "global.nodata.notavilable",
                    noDataSubtitle: "global.nodata.notavilable.subtitle",
                    aboveHeaderComponents: [
                        <DomainSelectionWrapper
                            key={props.tableSelectionKey}
                            tableSelectionKey={props.tableSelectionKey}
                            tableSelectionProperty={TABLE_SELECTION_PROPERTY}
                            createGroupButtonKey={"table.selection.create.group.button"}
                            addToGroupButtonKey={"table.selection.custom.industry.group"}
                            searchListsPlaceholderKey={"table.selection.search.industries.placeholder"}
                            keywordGapAnalysis={"table.selection.gap.analysis"}
                            keywordGapAnalysisTargetState={websiteKeywordsState}
                            entity={entity}
                        />,
                    ],
                    shouldEnrichRow: (props, index, e) => {
                        const openEnrich = e?.currentTarget?.parentNode.childNodes[0].className.includes("enrich");
                        if (openEnrich) {
                            allTrackers.trackEvent("Open", "Click", "Traffic Over Time/Expand");
                        }
                        return openEnrich;
                    },
                    get enrichedRowComponentHeight() {
                        return 600;
                    },
                    enrichedRowComponentProps: {
                        getSitesForApi,
                        propsInitialFilters: initialFilters,
                        isKeywordsGroup: props.isKeywordsGroup,
                        params,
                        pageNumber,
                        pageSize: TABLE_PAGE_SIZE,
                        visibleColumns: visibleColumnsEnrich,
                        enrichTableEndpoint,
                        transformDataEnrich,
                        removeLinkFromEnrichTable,
                        entity,
                        selectedPageTab,
                    },
                    onEnrichedRowClick: () => undefined,
                    syncEnrichedRowHeightToContent: true,
                    EnrichedRowComponent: OrganicTableEnriched,
                    get enrichedRowComponentClass() {
                        return `DropdownContent-container ${selectedRows?.length > 0 ? "selected" : ""}`;
                    },
                    get enrichedRowComponentAppendTo() {
                        return ".keywords-table";
                    },
                    customTableClass: "keywords-table",
                }}
                recordsField="records"
                totalRecordsField="TotalCount"
                onSort={onSort}
                pageIndent={1}
            >
                {(topComponentProps) => (
                    <TableTop
                        {...topComponentProps}
                        sort={sort}
                        tableEndpoint={tableEndpoint}
                        hideAddToDashboard={props.hideAddToDashboard}
                        hideExcel={props.hideExcel}
                        tableHeaderTooltip={tableHeaderTooltip}
                        dashboardMetric={props.dashboardMetric}
                    />
                )}
            </SWReactTableWrapperWithSelection>
        </TableWrapper>
    );
};
